import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';

import {
  Email as EmailIcon,
  Print as PrintIcon,
  Payment as PaymentIcon,
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  ThumbUp as ApproveIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { fetchBillById, emailBill, processPayment, approveBill, validatePeriodForTransaction } from '../../redux/thunks/billingThunks';
import { setCurrentBill } from '../../redux/slices/billingSlice';
import { useUserData } from '../../hooks/useUserData';
import { safeFormatDate } from '../../utils/dateUtils';
import LockIcon from '@mui/icons-material/Lock';

// Styled Components - Consistent with other pages
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.2)',
}));

const InfoCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
}));

const CompactButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  padding: '8px 16px',
  minWidth: 'auto',
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  color: 'white',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.grey[50],
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
  },
  '& .MuiTableCell-body': {
    fontSize: '0.875rem',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const BillDetails = () => {
  const { billId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Get user data using the new hook
  const userData = useUserData();
  
  const currentBill = useSelector(state => state.billing.currentBill);
  const loading = useSelector(state => state.billing.loading);
  const error = useSelector(state => state.billing.error);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [periodValidation, setPeriodValidation] = useState(null);
  const [periodCheckLoading, setPeriodCheckLoading] = useState(false);
  
  // Ref to track if we've already fetched this bill to prevent duplicate API calls
  const hasFetchedBill = useRef(false);

  // Reset the fetch flag when billId changes
  useEffect(() => {
    hasFetchedBill.current = false;
  }, [billId]);

  useEffect(() => {
    console.log('BillDetails useEffect triggered:', { billId, currentBillId: currentBill?.id, userUid: userData.user?.uid });
    
    if (userData.user?.uid) {
      // Prevent unnecessary API calls if we already have the current bill
      if (currentBill?.id === billId) {
        console.log('Bill already loaded, skipping API call');
        return;
      }
      
      // Prevent API calls if we're already loading
      if (loading) {
        console.log('Already loading, skipping API call');
        return;
      }
      
      // Prevent duplicate API calls for the same bill
      if (hasFetchedBill.current) {
        console.log('Bill already fetched, skipping API call');
        return;
      }
      
      console.log('Fetching bill details for:', billId);
      
      // Mark that we've fetched this bill
      hasFetchedBill.current = true;
      
      // Only pass the specific properties needed by the thunk
      const userDataForThunk = {
        user: userData.user,
        userRoles: userData.userRoles,
        uid: userData.uid,
        email: userData.email,
        name: userData.name,
        defaultFacility: userData.defaultFacility
      };
      dispatch(fetchBillById(billId, userDataForThunk));
    }
    
    // Cleanup function to clear current bill when component unmounts or billId changes
    return () => {
      if (currentBill?.id !== billId) {
        console.log('Clearing current bill on cleanup');
        dispatch(setCurrentBill(null));
      }
      // Reset the fetch flag on cleanup
      hasFetchedBill.current = false;
    };
  }, [dispatch, billId, currentBill?.id, loading, userData.user?.uid, userData.userRoles, userData.uid, userData.email, userData.name, userData.defaultFacility, userData.user]);

  // Check period status when bill is loaded
  useEffect(() => {
    const checkPeriodStatus = async () => {
      if (currentBill?.createdAt && currentBill?.facilityId) {
        setPeriodCheckLoading(true);
        try {
          const billDate = currentBill.createdAt instanceof Date 
            ? currentBill.createdAt 
            : new Date(currentBill.createdAt);
          const result = await dispatch(validatePeriodForTransaction(
            currentBill.facilityId,
            billDate,
            'payment'
          ));
          setPeriodValidation(result);
        } catch (error) {
          console.error('Error checking period status:', error);
          setPeriodValidation(null);
        } finally {
          setPeriodCheckLoading(false);
        }
      }
    };
    
    checkPeriodStatus();
  }, [dispatch, currentBill?.createdAt, currentBill?.facilityId]);

  // Check if editing is allowed based on period status
  const isEditingAllowed = periodValidation?.allowed !== false;
  const isPeriodSoftClosed = periodValidation?.status === 'soft_closed';
  const isPeriodHardClosed = periodValidation?.status === 'hard_closed';

  const handleEmailBill = async () => {
    try {
      await dispatch(emailBill(billId));
    } catch (error) {
      console.error('Error emailing bill:', error);
    }
  };

  const handleApproveBill = async () => {
    try {
      await dispatch(approveBill(billId));
      // Refresh bill data to show updated status
      const userDataForThunk = {
        user: userData.user,
        userRoles: userData.userRoles,
        uid: userData.uid,
        email: userData.email,
        name: userData.name,
        defaultFacility: userData.defaultFacility
      };
      await dispatch(fetchBillById(billId, userDataForThunk));
    } catch (error) {
      console.error('Error approving bill:', error);
    }
  };

  const handlePrintBill = () => {
    // Generate a clean invoice HTML for printing
    const subtotal = (currentBill.totalAmount || 0) - (currentBill.tax || 0) + (currentBill.discount || 0);
    
    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - ${currentBill.billNumber || currentBill.id}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              color: #333;
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
            }
            .invoice-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              border-bottom: 3px solid #667eea;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            .company-info h1 {
              color: #667eea;
              font-size: 24px;
              margin-bottom: 8px;
            }
            .company-info p {
              color: #666;
              font-size: 12px;
              line-height: 1.5;
            }
            .invoice-title {
              text-align: right;
            }
            .invoice-title h2 {
              font-size: 28px;
              color: #333;
              margin-bottom: 10px;
            }
            .invoice-title p {
              font-size: 13px;
              color: #666;
            }
            .invoice-title .bill-number {
              font-weight: 600;
              color: #667eea;
            }
            .bill-info {
              display: flex;
              justify-content: space-between;
              margin-bottom: 30px;
            }
            .bill-to, .bill-details {
              width: 48%;
            }
            .bill-to h3, .bill-details h3 {
              font-size: 12px;
              text-transform: uppercase;
              color: #999;
              margin-bottom: 10px;
              letter-spacing: 1px;
            }
            .bill-to p, .bill-details p {
              font-size: 14px;
              line-height: 1.6;
            }
            .bill-details {
              text-align: right;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 30px;
            }
            th {
              background-color: #667eea;
              color: white;
              padding: 12px 15px;
              text-align: left;
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            th:last-child {
              text-align: right;
            }
            td {
              padding: 12px 15px;
              border-bottom: 1px solid #eee;
              font-size: 14px;
            }
            td:last-child {
              text-align: right;
            }
            .totals-section {
              display: flex;
              justify-content: flex-end;
            }
            .totals {
              width: 300px;
            }
            .totals-row {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              font-size: 14px;
            }
            .totals-row.subtotal {
              border-bottom: 1px solid #eee;
            }
            .totals-row.total {
              border-top: 2px solid #667eea;
              margin-top: 10px;
              padding-top: 15px;
              font-size: 18px;
              font-weight: 700;
              color: #667eea;
            }
            .totals-row.balance {
              background-color: #fff3e0;
              padding: 10px;
              margin-top: 10px;
              border-radius: 4px;
            }
            .totals-row.balance.paid {
              background-color: #e8f5e9;
              color: #2e7d32;
            }
            .payment-history {
              margin-top: 30px;
              padding-top: 20px;
              border-top: 1px solid #eee;
            }
            .payment-history h3 {
              font-size: 14px;
              margin-bottom: 15px;
              color: #333;
            }
            .payment-history table th {
              background-color: #f5f5f5;
              color: #333;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #eee;
              text-align: center;
              font-size: 12px;
              color: #999;
            }
            .status-badge {
              display: inline-block;
              padding: 4px 12px;
              border-radius: 12px;
              font-size: 12px;
              font-weight: 600;
              text-transform: uppercase;
            }
            .status-paid {
              background-color: #e8f5e9;
              color: #2e7d32;
            }
            .status-approved {
              background-color: #e3f2fd;
              color: #1565c0;
            }
            .status-partial {
              background-color: #fff3e0;
              color: #ef6c00;
            }
            .status-pending {
              background-color: #fafafa;
              color: #757575;
            }
            @media print {
              body {
                padding: 20px;
              }
            }
          </style>
        </head>
        <body>
          <div class="invoice-header">
            <div class="company-info">
              <h1>QuantumLeap Healthcare</h1>
              <p>123 Medical Center Drive<br/>
              Healthcare City, HC 12345<br/>
              Phone: (555) 123-4567<br/>
              Email: billing@quantumleapcorp.com</p>
            </div>
            <div class="invoice-title">
              <h2>INVOICE</h2>
              <p class="bill-number">#${currentBill.billNumber || currentBill.id}</p>
              <p>
                <span class="status-badge status-${(currentBill.status || 'pending').toLowerCase()}">
                  ${(currentBill.status || 'pending').toUpperCase()}
                </span>
              </p>
            </div>
          </div>

          <div class="bill-info">
            <div class="bill-to">
              <h3>Bill To</h3>
              <p>
                <strong>${currentBill.patientName || 'N/A'}</strong><br/>
                Patient ID: ${currentBill.patientId || 'N/A'}
              </p>
            </div>
            <div class="bill-details">
              <h3>Invoice Details</h3>
              <p>
                <strong>Date:</strong> ${safeFormatDate(currentBill.createdAt, 'MMMM dd, yyyy')}<br/>
                <strong>Due Date:</strong> ${safeFormatDate(currentBill.dueDate, 'MMMM dd, yyyy')}<br/>
                <strong>Terms:</strong> ${currentBill.paymentTerms || 'Due on receipt'}
              </p>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit Price</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${(currentBill.items || []).map(item => `
                <tr>
                  <td>${item.description || ''}</td>
                  <td>${item.quantity || 0}</td>
                  <td>$${(item.unitPrice || 0).toFixed(2)}</td>
                  <td>$${(item.amount || 0).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="totals-section">
            <div class="totals">
              <div class="totals-row subtotal">
                <span>Subtotal:</span>
                <span>$${subtotal.toFixed(2)}</span>
              </div>
              ${(currentBill.tax || 0) > 0 ? `
                <div class="totals-row">
                  <span>Tax:</span>
                  <span>$${(currentBill.tax || 0).toFixed(2)}</span>
                </div>
              ` : ''}
              ${(currentBill.discount || 0) > 0 ? `
                <div class="totals-row">
                  <span>Discount:</span>
                  <span>-$${(currentBill.discount || 0).toFixed(2)}</span>
                </div>
              ` : ''}
              <div class="totals-row total">
                <span>Total:</span>
                <span>$${(currentBill.totalAmount || 0).toFixed(2)}</span>
              </div>
              <div class="totals-row">
                <span>Amount Paid:</span>
                <span>$${(currentBill.paidAmount || 0).toFixed(2)}</span>
              </div>
              <div class="totals-row balance ${remainingAmount <= 0 ? 'paid' : ''}">
                <span><strong>Balance Due:</strong></span>
                <span><strong>$${remainingAmount.toFixed(2)}</strong></span>
              </div>
            </div>
          </div>

          ${(currentBill.payments && currentBill.payments.length > 0) ? `
            <div class="payment-history">
              <h3>Payment History</h3>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Method</th>
                    <th>Reference</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${currentBill.payments.map(payment => `
                    <tr>
                      <td>${safeFormatDate(payment.date, 'MMM dd, yyyy')}</td>
                      <td style="text-transform: capitalize;">${(payment.method || '').replace('_', ' ')}</td>
                      <td>${payment.reference || 'N/A'}</td>
                      <td>$${(payment.amount || 0).toFixed(2)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}

          <div class="footer">
            <p>Thank you for choosing QuantumLeap Healthcare.</p>
            <p>For billing inquiries, please contact our billing department at billing@quantumleapcorp.com</p>
          </div>
        </body>
      </html>
    `;

    // Open a new window and print
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(invoiceHTML);
      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        // Close the window after a brief delay to allow print dialog to open
        setTimeout(() => {
          printWindow.close();
        }, 250);
      };
    }
  };

  const handlePaymentSubmit = async () => {
    if (!paymentAmount || !paymentMethod) {
      setPaymentError('Please fill in all required fields');
      return;
    }

    const amount = parseFloat(paymentAmount);
    if (amount <= 0 || amount > remainingAmount) {
      setPaymentError('Invalid payment amount');
      return;
    }

    try {
      const paymentData = {
        amount,
        method: paymentMethod
      };
      await dispatch(processPayment(billId, paymentData, userData));
      setPaymentDialogOpen(false);
      setPaymentAmount('');
      setPaymentMethod('');
      setPaymentError('');
    } catch {
      setPaymentError('Failed to process payment');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'approved':
        return 'info';
      case 'partial':
        return 'warning';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircleIcon fontSize="small" />;
      case 'approved':
        return <VerifiedIcon fontSize="small" />;
      case 'partial':
        return <WarningIcon fontSize="small" />;
      case 'pending':
        return <ScheduleIcon fontSize="small" />;
      default:
        return <ScheduleIcon fontSize="small" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
        <Alert severity="error" sx={{ maxWidth: '600px', mx: 'auto' }}>
          Error: {error}
        </Alert>
      </Box>
    );
  }

  if (!currentBill) {
    return (
      <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
        <Alert severity="warning" sx={{ maxWidth: '600px', mx: 'auto' }}>
          Bill not found
        </Alert>
      </Box>
    );
  }

  const remainingAmount = (currentBill.totalAmount || 0) - (currentBill.paidAmount || 0);

  return (
    <Box sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh', py: 3 }}>
      <Container maxWidth="lg">
        {/* Header Card */}
        <HeaderCard>
          <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
            <Grid container spacing={3} alignItems="center" justifyContent="space-between">
              <Grid item>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton
                    onClick={() => navigate('/billing')}
                    sx={{ color: 'white', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Box>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                      Bill Details
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      #{currentBill.billNumber || currentBill.id} • {currentBill.patientName}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Email Bill">
                    <CompactButton
                      startIcon={<EmailIcon sx={{ fontSize: 18 }} />}
                      onClick={handleEmailBill}
                      disabled={loading}
                    >
                      Email
                    </CompactButton>
                  </Tooltip>
                  <Tooltip title="Print Bill">
                    <CompactButton
                      startIcon={<PrintIcon sx={{ fontSize: 18 }} />}
                      onClick={handlePrintBill}
                      disabled={loading}
                    >
                      Print
                    </CompactButton>
                  </Tooltip>
                  {(currentBill.status || 'pending') === 'pending' && (
                    <Tooltip title={isPeriodHardClosed ? 'Period is closed - modifications not allowed' : 'Approve Bill for Claim Processing'}>
                      <span>
                        <CompactButton
                          startIcon={<ApproveIcon sx={{ fontSize: 18 }} />}
                          onClick={handleApproveBill}
                          sx={{
                            backgroundColor: isPeriodHardClosed ? 'grey.500' : 'info.main',
                            '&:hover': { backgroundColor: isPeriodHardClosed ? 'grey.600' : 'info.dark' }
                          }}
                          disabled={loading || isPeriodHardClosed}
                        >
                          Approve Bill
                        </CompactButton>
                      </span>
                    </Tooltip>
                  )}
                  {(currentBill.status || 'pending') !== 'paid' && (
                    <Tooltip title={isPeriodHardClosed ? 'Period is closed - modifications not allowed' : 'Process Payment'}>
                      <span>
                        <CompactButton
                          startIcon={isPeriodHardClosed ? <LockIcon sx={{ fontSize: 18 }} /> : <PaymentIcon sx={{ fontSize: 18 }} />}
                          onClick={() => setPaymentDialogOpen(true)}
                          disabled={isPeriodHardClosed}
                          sx={{
                            backgroundColor: isPeriodHardClosed ? 'grey.500' : 'success.main',
                            '&:hover': { backgroundColor: isPeriodHardClosed ? 'grey.600' : 'success.dark' }
                          }}
                        >
                          Process Payment
                        </CompactButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </HeaderCard>

        {/* Period Status Warning */}
        {isPeriodSoftClosed && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: '12px' }} icon={<WarningIcon />}>
            <strong>Soft Close Warning:</strong> {periodValidation?.message || 'This billing period is in soft close. Modifications are allowed but will be locked soon.'}
          </Alert>
        )}
        {isPeriodHardClosed && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} icon={<LockIcon />}>
            <strong>Period Closed:</strong> {periodValidation?.message || 'This billing period is closed. No modifications are allowed.'}
          </Alert>
        )}

        {/* Bill Summary Cards - Compact */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <InfoCard>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ 
                    p: 1, 
                    borderRadius: '8px', 
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <AccountBalanceIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography color="textSecondary" sx={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.2 }}>
                      Total Amount
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.2 }}>
                      ${(currentBill.totalAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </InfoCard>
          </Grid>
          <Grid item xs={12} sm={4}>
            <InfoCard>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ 
                    p: 1, 
                    borderRadius: '8px', 
                    backgroundColor: 'success.light',
                    color: 'success.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography color="textSecondary" sx={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.2 }}>
                      Amount Paid
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main', lineHeight: 1.2 }}>
                      ${(currentBill.paidAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </InfoCard>
          </Grid>
          <Grid item xs={12} sm={4}>
            <InfoCard>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ 
                    p: 1, 
                    borderRadius: '8px', 
                    backgroundColor: remainingAmount > 0 ? 'warning.light' : 'success.light',
                    color: remainingAmount > 0 ? 'warning.contrastText' : 'success.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <WarningIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography color="textSecondary" sx={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.2 }}>
                      Remaining Balance
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: remainingAmount > 0 ? 'warning.main' : 'success.main', lineHeight: 1.2 }}>
                      ${remainingAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </InfoCard>
          </Grid>
        </Grid>

        {/* Bill Information */}
        <Grid container spacing={3}>
          {/* Patient Information */}
          <Grid item xs={12} md={6}>
            <InfoCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PersonIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Patient Information
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="textSecondary">Name:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{currentBill.patientName}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="textSecondary">Patient ID:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{currentBill.patientId}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                          <Typography variant="body2" color="textSecondary">Bill Date:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {safeFormatDate(currentBill.createdAt, 'MMM dd, yyyy')}
                      </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                          <Typography variant="body2" color="textSecondary">Due Date:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {safeFormatDate(currentBill.dueDate, 'MMM dd, yyyy')}
                      </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="textSecondary">Payment Terms:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{currentBill.paymentTerms || 'N/A'}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </InfoCard>
          </Grid>

          {/* Bill Status */}
          <Grid item xs={12} md={6}>
            <InfoCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ReceiptIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Bill Status
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="textSecondary">Status:</Typography>
                    <Chip 
                      icon={getStatusIcon(currentBill.status || 'pending')}
                      label={currentBill.status || 'pending'} 
                      color={getStatusColor(currentBill.status || 'pending')}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="textSecondary">Subtotal:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      ${((currentBill.totalAmount || 0) - (currentBill.tax || 0) - (currentBill.discount || 0)).toFixed(2)}
                    </Typography>
                  </Box>
                  {currentBill.tax > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="textSecondary">Tax:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        ${(currentBill.tax || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  {currentBill.discount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="textSecondary">Discount:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                        -${(currentBill.discount || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Total:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      ${(currentBill.totalAmount || 0).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </InfoCard>
          </Grid>
          
          {/* Bill Items */}
          <Grid item xs={12}>
            <InfoCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <ReceiptIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Bill Items
                  </Typography>
                </Box>
                <TableContainer>
                  <StyledTable>
                    <TableHead>
                      <TableRow>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentBill.items && currentBill.items.map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.description}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{item.quantity}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              ${(item.unitPrice || 0).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              ${(item.amount || 0).toFixed(2)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </StyledTable>
                </TableContainer>
              </CardContent>
            </InfoCard>
          </Grid>

          {/* Payment History */}
          {currentBill.payments && currentBill.payments.length > 0 && (
            <Grid item xs={12}>
              <InfoCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <PaymentIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Payment History
                    </Typography>
                  </Box>
                  <TableContainer>
                    <StyledTable>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell>Method</TableCell>
                          <TableCell>Reference</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentBill.payments.map((payment, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Typography variant="body2">
                                {safeFormatDate(payment.date, 'MMM dd, yyyy')}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                                ${(payment.amount || 0).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={payment.method} 
                                size="small" 
                                variant="outlined"
                                sx={{ textTransform: 'capitalize' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="textSecondary">
                                {payment.reference || 'N/A'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </StyledTable>
                  </TableContainer>
                </CardContent>
              </InfoCard>
            </Grid>
          )}
        </Grid>

        {/* Payment Dialog */}
        <Dialog 
          open={paymentDialogOpen} 
          onClose={() => setPaymentDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PaymentIcon color="primary" />
              Process Payment
            </Box>
          </DialogTitle>
          <DialogContent>
            {paymentError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {paymentError}
              </Alert>
            )}
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Remaining balance: <strong>${remainingAmount.toFixed(2)}</strong>
              </Typography>
            </Box>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Payment Amount"
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  inputProps={{ 
                    min: 0, 
                    max: remainingAmount, 
                    step: 0.01 
                  }}
                  helperText="Enter the amount to be paid"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label="Payment Method"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <MenuItem value="credit_card">Credit Card</MenuItem>
                  <MenuItem value="debit_card">Debit Card</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="check">Check</MenuItem>
                  <MenuItem value="insurance">Insurance</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button 
              onClick={() => setPaymentDialogOpen(false)}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePaymentSubmit} 
              variant="contained"
              startIcon={<PaymentIcon />}
              disabled={!paymentAmount || !paymentMethod}
            >
              Process Payment
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default BillDetails;
